import { useDisclosure } from '@mantine/hooks';

import { EOnboardingTaskType } from '@feathr/blackbox';

export interface IUseMilestoneTaskModalsReturn {
  modalHandlers: {
    openSetupSuperPixelModal: () => void;
    closeSetupSuperPixelModal: () => void;
    openCreateFirstProjectModal: () => void;
    closeCreateFirstProjectModal: () => void;
    openBuildFirstGroupModal: () => void;
    closeBuildFirstGroupModal: () => void;
    openCreateEmailCampaignModal: () => void;
    closeCreateEmailCampaignModal: () => void;
    openCreateEmailTemplateModal: () => void;
    closeCreateEmailTemplateModal: () => void;
    openInviteUserModal: () => void;
    closeInviteUserModal: () => void;
    openModalForTask: (taskType: EOnboardingTaskType) => void;
    closeAllModals: () => void;
  };
  modalStates: {
    isSetupSuperPixelModalOpen: boolean;
    isCreateFirstProjectModalOpen: boolean;
    isBuildFirstGroupModalOpen: boolean;
    isCreateEmailTemplateModalOpen: boolean;
    isCreateEmailCampaignModalOpen: boolean;
    isInviteUserModalOpen: boolean;
  };
  getModalStateForTask: (taskType: EOnboardingTaskType) => boolean;
}

/**
 * The useMilestoneTaskModals hook manages modal state for onboarding milestone tasks.
 * It provides a centralized way to open/close modals for specific task types that require
 * additional information gathering before proceeding with the task.
 */
function useMilestoneTaskModals(): IUseMilestoneTaskModalsReturn {
  // Define all modals in a map for easier management
  const modalMap = {
    [EOnboardingTaskType.SetupSuperPixel]: useDisclosure(false),
    [EOnboardingTaskType.CreateFirstProject]: useDisclosure(false),
    [EOnboardingTaskType.BuildFirstGroup]: useDisclosure(false),
    [EOnboardingTaskType.CreateEmailTemplate]: useDisclosure(false),
    [EOnboardingTaskType.PublishFirstEmailCampaign]: useDisclosure(false),
    [EOnboardingTaskType.InviteOtherUsers]: useDisclosure(false),
  } as const;

  // Extract state and handlers from the modal map
  const [
    isSetupSuperPixelModalOpen,
    { open: openSetupSuperPixelModal, close: closeSetupSuperPixelModal },
  ] = modalMap[EOnboardingTaskType.SetupSuperPixel];

  const [
    isCreateFirstProjectModalOpen,
    { open: openCreateFirstProjectModal, close: closeCreateFirstProjectModal },
  ] = modalMap[EOnboardingTaskType.CreateFirstProject];

  const [
    isBuildFirstGroupModalOpen,
    { open: openBuildFirstGroupModal, close: closeBuildFirstGroupModal },
  ] = modalMap[EOnboardingTaskType.BuildFirstGroup];

  const [
    isCreateEmailTemplateModalOpen,
    { open: openCreateEmailTemplateModal, close: closeCreateEmailTemplateModal },
  ] = modalMap[EOnboardingTaskType.CreateEmailTemplate];

  const [
    isCreateEmailCampaignModalOpen,
    { open: openCreateEmailCampaignModal, close: closeCreateEmailCampaignModal },
  ] = modalMap[EOnboardingTaskType.PublishFirstEmailCampaign];

  const [isInviteUserModalOpen, { open: openInviteUserModal, close: closeInviteUserModal }] =
    modalMap[EOnboardingTaskType.InviteOtherUsers];

  // Generic function to open modal based on task type
  function openModalForTask(taskType: EOnboardingTaskType): void {
    const modal = modalMap[taskType];
    if (modal) {
      modal[1].open();
    }
  }

  // Function to close all modals
  function closeAllModals(): void {
    Object.values(modalMap).forEach(([, { close }]) => close());
  }

  // Helper function to get modal state for a specific task
  function getModalStateForTask(taskType: EOnboardingTaskType): boolean {
    const modal = modalMap[taskType];
    return modal ? modal[0] : false;
  }

  return {
    modalHandlers: {
      openSetupSuperPixelModal,
      closeSetupSuperPixelModal,
      openCreateFirstProjectModal,
      closeCreateFirstProjectModal,
      openBuildFirstGroupModal,
      closeBuildFirstGroupModal,
      openCreateEmailTemplateModal,
      closeCreateEmailTemplateModal,
      openCreateEmailCampaignModal,
      closeCreateEmailCampaignModal,
      openInviteUserModal,
      closeInviteUserModal,
      openModalForTask,
      closeAllModals,
    },
    modalStates: {
      isSetupSuperPixelModalOpen,
      isCreateFirstProjectModalOpen,
      isBuildFirstGroupModalOpen,
      isCreateEmailTemplateModalOpen,
      isCreateEmailCampaignModalOpen,
      isInviteUserModalOpen,
    },
    getModalStateForTask,
  };
}

export default useMilestoneTaskModals;
